import React, { useState, useRef, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { X, PenTool, Type, Trash2, ChevronLeft, ChevronRight, Save, Send } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

// Configure pdf.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const FIELD_DEFAULTS = {
  signature: { width: 28, height: 8, label: "Signature" },
  initials:  { width: 14, height: 8, label: "Initials"  },
};

/**
 * DocumentSigningFieldEditor
 * Full-screen modal where EOs place signature/initials fields on a PDF.
 *
 * Props:
 *   document       — requiredDocument object (must have downloadURL, title, id)
 *   existingFields — array of saved signatureFields (may be empty)
 *   onSave(fields) — called with the final fields array when EO saves
 *   onSaveAndSend(fields, vendorIds, deadline) — called when EO assigns & sends
 *   approvedVendors — [{ id, vendorName }] list to assign
 *   onClose        — close the modal
 */
const DocumentSigningFieldEditor = ({
  document,
  existingFields = [],
  onSave,
  onSaveAndSend,
  approvedVendors = [],
  onClose,
}) => {
  const [fields, setFields] = useState(existingFields);
  const [activeFieldType, setActiveFieldType] = useState("signature");
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [pdfLoadError, setPdfLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAssignPanel, setShowAssignPanel] = useState(false);
  const [assignedVendorIds, setAssignedVendorIds] = useState([]);
  const [signingDeadline, setSigningDeadline] = useState("");
  const pageContainerRef = useRef(null);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoadError(null);
  };

  const onPageLoadSuccess = (page) => {
    const viewport = page.getViewport({ scale: 1 });
    if (pageContainerRef.current) {
      const containerWidth = pageContainerRef.current.clientWidth;
      const scale = containerWidth / viewport.width;
      setPageSize({
        width: containerWidth,
        height: viewport.height * scale,
      });
    }
  };

  const handlePageClick = useCallback(
    (e) => {
      if (!pageContainerRef.current || !pageSize.width) return;
      const rect = pageContainerRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      const defaults = FIELD_DEFAULTS[activeFieldType];
      // Center field on click, stored as % of page
      const x = Math.max(0, Math.min(100 - defaults.width, (clickX / pageSize.width) * 100 - defaults.width / 2));
      const y = Math.max(0, Math.min(100 - defaults.height, (clickY / pageSize.height) * 100 - defaults.height / 2));

      const newField = {
        id: uuidv4(),
        type: activeFieldType,
        page: currentPage,
        x,
        y,
        width: defaults.width,
        height: defaults.height,
        label: defaults.label,
        required: true,
      };

      setFields((prev) => [...prev, newField]);
    },
    [activeFieldType, currentPage, pageSize]
  );

  const removeField = (fieldId) => {
    setFields((prev) => prev.filter((f) => f.id !== fieldId));
  };

  const toggleVendor = (vendorId) => {
    setAssignedVendorIds((prev) =>
      prev.includes(vendorId) ? prev.filter((id) => id !== vendorId) : [...prev, vendorId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(fields);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndSend = async () => {
    setSaving(true);
    try {
      await onSaveAndSend(
        fields,
        assignedVendorIds,
        signingDeadline ? new Date(signingDeadline) : null
      );
    } finally {
      setSaving(false);
    }
  };

  const currentPageFields = fields.filter((f) => f.page === currentPage);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-grey-200 px-6 py-4 flex items-center justify-between shrink-0">
        <div>
          <h2 className="text-lg font-semibold text-grey-900">Place Signature Fields</h2>
          <p className="text-sm text-grey-500">{document.title} · Click on the document to place a field</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-grey-500">{fields.length} field{fields.length !== 1 ? "s" : ""} placed</span>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition font-medium text-sm disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            Save Draft
          </button>
          <button
            onClick={() => setShowAssignPanel(true)}
            disabled={fields.length === 0 || saving}
            className="flex items-center gap-2 px-4 py-2 bg-pebble-red text-white rounded-lg hover:bg-red-700 transition font-medium text-sm disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            Assign & Send
          </button>
          <button onClick={onClose} className="p-2 hover:bg-grey-100 rounded-lg transition">
            <X className="w-5 h-5 text-grey-500" />
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left toolbar */}
        <div className="w-56 bg-white border-r border-grey-200 p-4 flex flex-col gap-4 shrink-0 overflow-y-auto">
          <div>
            <p className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-2">Add Field</p>
            <div className="space-y-2">
              <button
                onClick={() => setActiveFieldType("signature")}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg border-2 transition text-sm font-medium ${
                  activeFieldType === "signature"
                    ? "border-pebble-red bg-red-50 text-pebble-red"
                    : "border-grey-200 text-grey-700 hover:border-grey-300"
                }`}
              >
                <PenTool className="w-4 h-4" />
                Signature
              </button>
              <button
                onClick={() => setActiveFieldType("initials")}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg border-2 transition text-sm font-medium ${
                  activeFieldType === "initials"
                    ? "border-pebble-red bg-red-50 text-pebble-red"
                    : "border-grey-200 text-grey-700 hover:border-grey-300"
                }`}
              >
                <Type className="w-4 h-4" />
                Initials
              </button>
            </div>
            <p className="text-xs text-grey-400 mt-2">Click anywhere on the document to place the selected field</p>
          </div>

          {fields.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-grey-500 uppercase tracking-wide mb-2">
                Placed Fields ({fields.length})
              </p>
              <div className="space-y-1.5">
                {fields.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center justify-between px-2 py-1.5 bg-grey-50 rounded-lg text-xs"
                  >
                    <span className="text-grey-700 truncate">
                      {f.label} · p{f.page}
                    </span>
                    <button
                      onClick={() => removeField(f.id)}
                      className="ml-1 p-0.5 text-grey-400 hover:text-red-500 transition shrink-0"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* PDF canvas area */}
        <div className="flex-1 bg-grey-100 overflow-auto flex flex-col items-center p-6 gap-4">
          {/* Page navigation */}
          {numPages && numPages > 1 && (
            <div className="flex items-center gap-3 bg-white rounded-lg px-4 py-2 shadow-sm">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1 hover:bg-grey-100 rounded disabled:opacity-40 transition"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium text-grey-700">
                Page {currentPage} of {numPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
                disabled={currentPage === numPages}
                className="p-1 hover:bg-grey-100 rounded disabled:opacity-40 transition"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* PDF page with field overlay */}
          <div className="relative shadow-xl bg-white" style={{ width: "100%", maxWidth: 780 }}>
            <div
              ref={pageContainerRef}
              className="relative cursor-crosshair select-none"
              onClick={handlePageClick}
              style={{ minHeight: pageSize.height || 200 }}
            >
              <Document
                file={document.downloadURL}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={(err) => setPdfLoadError(err.message)}
                loading={
                  <div className="flex items-center justify-center py-20 text-grey-500 text-sm">
                    Loading PDF...
                  </div>
                }
              >
                {!pdfLoadError && (
                  <Page
                    pageNumber={currentPage}
                    width={pageContainerRef.current?.clientWidth || 780}
                    onLoadSuccess={onPageLoadSuccess}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                )}
              </Document>

              {/* Field overlays */}
              {pageSize.width > 0 &&
                currentPageFields.map((field) => (
                  <div
                    key={field.id}
                    className="absolute border-2 border-pebble-red bg-red-50/60 rounded flex items-center justify-center group"
                    style={{
                      left: `${field.x}%`,
                      top: `${field.y}%`,
                      width: `${field.width}%`,
                      height: `${field.height}%`,
                      pointerEvents: "auto",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="text-xs font-semibold text-pebble-red select-none">
                      {field.label}
                    </span>
                    <button
                      onClick={() => removeField(field.id)}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full items-center justify-center hidden group-hover:flex"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}

              {pdfLoadError && (
                <div className="absolute inset-0 flex items-center justify-center bg-grey-50">
                  <p className="text-sm text-grey-500">Could not load PDF preview. Fields can still be saved.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assign & Send panel */}
      {showAssignPanel && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-grey-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-grey-900">Assign to Vendors</h3>
                <p className="text-sm text-grey-500">Select which vendors need to sign this document</p>
              </div>
              <button onClick={() => setShowAssignPanel(false)} className="p-1 hover:bg-grey-100 rounded-lg">
                <X className="w-5 h-5 text-grey-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {approvedVendors.length > 0 ? (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-grey-700">Approved vendors</p>
                    <button
                      onClick={() =>
                        setAssignedVendorIds(
                          assignedVendorIds.length === approvedVendors.length
                            ? []
                            : approvedVendors.map((v) => v.id)
                        )
                      }
                      className="text-xs text-pebble-red hover:underline"
                    >
                      {assignedVendorIds.length === approvedVendors.length ? "Deselect all" : "Select all"}
                    </button>
                  </div>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {approvedVendors.map((vendor) => (
                      <label key={vendor.id} className="flex items-center gap-3 p-2 hover:bg-grey-50 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={assignedVendorIds.includes(vendor.id)}
                          onChange={() => toggleVendor(vendor.id)}
                          className="w-4 h-4 text-pebble-red border-grey-300 rounded"
                        />
                        <span className="text-sm text-grey-800">{vendor.vendorName || vendor.id}</span>
                      </label>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-grey-500">No approved vendors yet. Document will be assigned to all vendors when they are approved.</p>
              )}

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">
                  Signing Deadline <span className="text-grey-400">(optional)</span>
                </label>
                <input
                  type="date"
                  value={signingDeadline}
                  onChange={(e) => setSigningDeadline(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-pebble-red focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div className="p-6 border-t border-grey-200 flex gap-3">
              <button
                onClick={() => setShowAssignPanel(false)}
                className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition font-medium text-sm"
              >
                Back
              </button>
              <button
                onClick={handleSaveAndSend}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-pebble-red text-white rounded-lg hover:bg-red-700 transition font-medium text-sm disabled:opacity-50"
              >
                {saving ? "Sending..." : "Send for Signature"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentSigningFieldEditor;
